'use client';

import { useCallback, useEffect, useRef } from 'react';

import { postAdEventReq } from '../api';
import { getAdImpressionStorageKey, getAdSessionId } from '../utils';

type UseAdImpressionParams = {
  campaignId?: number | null;
  enabled?: boolean;
};

export const useAdImpression = ({ campaignId = null, enabled = false }: UseAdImpressionParams) => {
  const elementRef = useRef<Element | null>(null);
  const hasTrackedRef = useRef(false);
  const observerRef = useRef<IntersectionObserver | null>(null);

  useEffect(() => {
    hasTrackedRef.current = false;
  }, [campaignId]);

  const observeNode = useCallback(
    (targetNode: Element) => {
      if (!enabled || campaignId == null || hasTrackedRef.current) {
        return;
      }

      const impressionKey = getAdImpressionStorageKey(campaignId);

      if (sessionStorage.getItem(impressionKey)) {
        hasTrackedRef.current = true;
        return;
      }

      observerRef.current?.disconnect();

      const observer = new IntersectionObserver(
        (entries) => {
          const [entry] = entries;

          if (!entry?.isIntersecting || hasTrackedRef.current) {
            return;
          }

          hasTrackedRef.current = true;
          sessionStorage.setItem(impressionKey, '1');
          observer.disconnect();

          const sessionId = getAdSessionId();

          if (!sessionId) {
            return;
          }

          void postAdEventReq(campaignId, 'impression', sessionId);
        },
        { threshold: 0.5 },
      );

      observerRef.current = observer;
      observer.observe(targetNode);
    },
    [campaignId, enabled],
  );

  const setElementRef = useCallback(
    (node: Element | null) => {
      elementRef.current = node;

      if (node) {
        observeNode(node);
      } else {
        observerRef.current?.disconnect();
        observerRef.current = null;
      }
    },
    [observeNode],
  );

  useEffect(() => {
    const targetNode = elementRef.current;

    if (!targetNode) {
      return;
    }

    observeNode(targetNode);

    return () => {
      observerRef.current?.disconnect();
      observerRef.current = null;
    };
  }, [observeNode]);

  return { elementRef: setElementRef };
};
